import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getUsers, User } from '../../api/admin';
import UserActions from './UserActions';

const UserList: React.FC = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [search, setSearch] = useState('');
  const [searchInput, setSearchInput] = useState('');

  const fetchUsers = async () => {
    setLoading(true);
    setError('');
    try {
      const response = await getUsers(page, 20, search);
      setUsers(response.users);
      setTotalPages(response.total_pages);
    } catch (err) {
      setError('Failed to load users');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [page, search]);

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setSearch(searchInput);
    setPage(1);
  };

  const handleUserUpdate = () => {
    // Refresh the user list after an action
    fetchUsers();
  };

  if (loading && users.length === 0) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="text-lg text-zinc-400">Loading users...</div>
      </div>
    );
  }

  return (
    <div>
      <div className="mb-6">
        <h2 className="text-xl font-bold mb-4 text-zinc-100">User Management</h2>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-4 flex gap-2">
          <input
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by email..."
            className="input-dark flex-1 px-4 py-2 rounded-lg"
          />
          <button
            type="submit"
            className="btn-primary px-4 py-2 rounded-lg"
          >
            Search
          </button>
          {search && (
            <button
              type="button"
              onClick={() => {
                setSearch('');
                setSearchInput('');
                setPage(1);
              }}
              className="btn-secondary px-4 py-2 rounded-lg"
            >
              Clear
            </button>
          )}
        </form>

        {error && (
          <div className="alert alert-error mb-4">
            {error}
          </div>
        )}

        {/* Users Table */}
        <div className="card-dark rounded-xl overflow-hidden">
          <table className="table-dark">
            <thead>
              <tr>
                <th>Email</th>
                <th>Subscription</th>
                <th>Claims Tier</th>
                <th>Role</th>
                <th>Created</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {users.map((user) => (
                <tr key={user.id}>
                  <td>
                    <button
                      onClick={() => navigate(`/admin/user/${encodeURIComponent(user.email)}`)}
                      className="text-amber-400 hover:text-amber-300 hover:underline text-left font-medium"
                    >
                      {user.email}
                    </button>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        user.subscription_active
                          ? 'badge-success'
                          : 'badge-danger'
                      }`}
                    >
                      {user.subscription_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <span
                      className={`badge ${
                        user.claims_tier === 'premium'
                          ? 'bg-purple-500/20 text-purple-400 border border-purple-500/30'
                          : user.claims_tier === 'free'
                          ? 'badge-info'
                          : 'bg-zinc-500/20 text-zinc-400 border border-zinc-500/30'
                      }`}
                    >
                      {user.claims_tier}
                    </span>
                  </td>
                  <td>
                    {user.admin ? (
                      <span className="badge badge-amber">
                        Admin
                      </span>
                    ) : (
                      <span className="text-zinc-400">User</span>
                    )}
                  </td>
                  <td className="text-zinc-400">
                    {new Date(user.created_at).toLocaleDateString()}
                  </td>
                  <td>
                    <UserActions user={user} onUpdate={handleUserUpdate} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {totalPages > 1 && (
          <div className="mt-4 flex justify-between items-center">
            <button
              onClick={() => setPage(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn-secondary px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Previous
            </button>
            <span className="text-sm text-zinc-400">
              Page {page} of {totalPages}
            </span>
            <button
              onClick={() => setPage(Math.min(totalPages, page + 1))}
              disabled={page === totalPages}
              className="btn-secondary px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Next
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default UserList;
